import { ComponentType, useContext, useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes, Navigate } from 'react-router-dom';
import {
    CssBaseline,
    Box,
    StyledEngineProvider,
    ThemeProvider,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { getDateLocale, getDatePickerLocaleText } from './lib/dateLocale';
import sporttiaTheme from './theme/theme';
import {
    MutationCache,
    QueryCache,
    QueryClient,
    QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from 'react-error-boundary';
import { AppProvider, AppContext } from './sporttia/all';
import { FAB_BOTTOM, FAB_SIZE } from './sporttia/buttons/SttButtonFab';
import { getPath, paths } from './pages/Pages';
import GlobalCSS from './styles/GlobalCSS';
import EnvironmentOverlay from './layout/EnvironmentOverlay';
import RedirectToLogin from './components/RedirectToLogin';
import UserAccessPopup from './components/popups/UserAccessPopup';
import SCLayout from './layout/sc/SCLayout';
import constants from './config/constants';
import generalConfig from './config/general';
import FullPageError from './components/error/FullPageError';
import ProtectedElement from './components/permissions/ProtectedElement';
import SttDisabledApiPageError from './components/error/SttDisabledApiPageError';
import { SSEProvider } from './layout/SSEProvider';
import { useDevAutoImpersonate } from './lib/dev/useDevAutoImpersonate';
import Login from './pages/public/Login';
import RecoverPassword from './pages/public/RecoverPassword';
import Impersonate from 'src/pages/public/Impersonate';

/*  El chat de tawk vive en la misma esquina que el botón de acción de las
    páginas de listado, así que se sube justo por encima de él en lugar de
    taparlo. El hueco se calcula desde la propia geometría del botón para que
    los dos no se desincronicen si algún día se mueve.
*/
const CHAT_Y_OFFSET = FAB_BOTTOM + FAB_SIZE + 14;

/**
 * Public routes without layout (login, recover password)
 */
function PublicRoutes() {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Routes>
                <Route path="/impersonate" element={<Impersonate />} />
                <Route path="/login" element={<Login />} />
                <Route path="/recover" element={<RecoverPassword />} />
                <Route path="*" element={<RedirectToLogin />} />
            </Routes>
        </Box>
    );
}

/**
 * Protected routes with full layout (header + sidebar)
 */
function ProtectedRoutes() {
    const cxt = useContext(AppContext)!;

    // Build protected routes
    const routes = paths
        .filter((path: any) => {
            // Exclude public routes
            return path.id !== 'login' && path.id !== 'recoverPassword';
        })
        .map((path: any) => {
            if ('protected' in path && path.protected) {
                const roleProps = {
                    ...('role' in path && { routeRole: path.role }),
                    ...('role' in path &&
                        cxt.user &&
                        cxt.user?.role && { userRole: cxt.user?.role }),
                };

                return (
                    <Route
                        key={path.id}
                        path={path.path}
                        element={
                            <ProtectedElement
                                exact
                                path={path.path}
                                component={path.component as ComponentType}
                                {...roleProps}
                            />
                        }
                    />
                );
            }

            const PageComponent = path.component as ComponentType;
            return (
                <Route
                    key={path.id}
                    path={path.path}
                    element={<PageComponent />}
                />
            );
        });

    const socketMessages = cxt.getSocketMessages();
    const userAccessPopup = (
        <UserAccessPopup
            visible={socketMessages.length > 0}
            onDelete={cxt.deleteReceivedMessage}
            messages={socketMessages}
        />
    );

    return (
        <SSEProvider>
            <SCLayout>
                <Routes>
                    {/* Redirect root to timetable */}
                    <Route
                        path="/"
                        element={<Navigate to={getPath('timetable')} replace />}
                    />
                    {routes}
                </Routes>
                {userAccessPopup}
            </SCLayout>
        </SSEProvider>
    );
}

/**
 * Main content router - decides between public and protected routes
 */
function SporttiaContent() {
    const cxt = useContext(AppContext)!;

    // If user is logged in, show protected routes with layout
    if (cxt.logged && cxt.user?.role === constants.roles.sportcenter) {
        return <ProtectedRoutes />;
    }

    // Otherwise show public routes without layout
    return <PublicRoutes />;
}

interface ApiError {
    error?: {
        status?: number;
    };
}

/**
 * Main App component
 */
export default function App() {
    const [hasError503, setHasError503] = useState(false);

    // Inicio de sesión automático en desarrollo (no-op en cualquier otro sitio)
    useDevAutoImpersonate();

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
                retry: false,
            },
        },
        queryCache: new QueryCache({
            onError: (err: any) => {
                const parsedError = err as ApiError;

                if (parsedError?.error?.status === 503) setHasError503(true);
            },
        }),
        mutationCache: new MutationCache({
            onError: (err: any) => {
                const parsedError = err as ApiError;

                if (parsedError?.error?.status === 503) setHasError503(true);
            },
        }),
    });

    // Load the tawk script but only in PRO and not using the embed widget.
    useEffect(() => {
        if (
            generalConfig.environment === 'PRO' &&
            !window.location.href.match(/\/[\d]+\/widget$/)
        ) {
            // `customStyle` tiene que estar puesto antes de que el script se
            // cargue: el widget lo lee al arrancar para decidir dónde pintarse.
            // `br` = bottom right (por defecto tawk lo coloca abajo a la
            // izquierda desde su panel de configuración).
            window.Tawk_API = {
                ...window.Tawk_API,
                customStyle: {
                    visibility: {
                        desktop: {
                            position: 'br',
                            xOffset: 20,
                            yOffset: CHAT_Y_OFFSET,
                        },
                        mobile: {
                            position: 'br',
                            xOffset: 10,
                            yOffset: CHAT_Y_OFFSET,
                        },
                    },
                },
            };

            const script = document.createElement('script');
            script.src =
                'https://embed.tawk.to/56cd89c76cb3a9164247a9fb/default';
            script.charset = 'UTF-8';
            script.async = true;
            script.setAttribute('crossorigin', '*');
            script.setAttribute('id', 'tawk-script');
            document.body.appendChild(script);

            // Return a callback to offload the script (this is called on component unmount, which should be never, but just in case...)
            return () => {
                const tawkScript = document.getElementById('tawk-script');
                if (tawkScript?.parentNode) {
                    tawkScript.parentNode.removeChild(tawkScript);
                }
            };
        }
    }, []);

    return (
        // `enableCssLayer` mete los estilos de MUI en @layer mui, declarada en
        // index.html entre `base` y `utilities`: así MUI manda sobre los estilos
        // de elemento y Tailwind manda sobre MUI. `injectFirst` los coloca en el
        // punto de inserción de emotion, justo debajo de esa declaración.
        <StyledEngineProvider injectFirst enableCssLayer>
            {/* `modeStorageKey="theme"` es la clave que usa next-themes en
                sporttia-competitions, con el mismo vocabulario
                (light/dark/system): al servirse las dos desde el mismo dominio,
                el tema elegido en una se respeta en la otra. */}
            <ThemeProvider
                theme={sporttiaTheme}
                defaultMode="light"
                modeStorageKey="theme"
                disableTransitionOnChange
            >
                <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={getDateLocale()}
                    localeText={getDatePickerLocaleText()}
                >
                    <GlobalCSS />
                    <QueryClientProvider client={queryClient}>
                        <AppProvider>
                            {/* Dentro de AppProvider: el chip de ADMIN sólo se
                                afirma con sesión abierta, para no etiquetar el
                                login con una marca olvidada. */}
                            <EnvironmentOverlay />
                            {hasError503 ? (
                                <SttDisabledApiPageError />
                            ) : (
                                <ErrorBoundary fallback={<FullPageError />}>
                                    <CssBaseline />
                                    <BrowserRouter>
                                        <SporttiaContent />
                                    </BrowserRouter>
                                </ErrorBoundary>
                            )}
                        </AppProvider>
                        <ReactQueryDevtools initialIsOpen />
                    </QueryClientProvider>
                </LocalizationProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}
